import { Button, Card, CardHeader, useToast } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createCategory, createExpense, createPayee, fetchCategories, fetchPayees, type ExpenseStatus } from "../../api/expenses";
import { uploadAttachment } from "../../api/reports";
import { useServices } from "../../app/services";
import { Field, Gate, MoneyInput, PageHead, css as s, errorText, isDate, parseMoneyInput, useIntentKey, useScope } from "../payroll/kit";

/** Payment modes: the API's defaults (settings.expenses.paymentModes has no read route yet); the server still checks them. */
export const MODES = ["cash", "upi", "bank_transfer", "card", "cheque"];
const MAX_BYTES = 10 * 1024 * 1024;
const TYPES = ["application/pdf", "image/jpeg", "image/png"];

/** `/expenses/new`. One Idempotency-Key per open form, reused on retry. The attachment goes up after the expense exists. */
export function NewExpensePage() {
  const sc = useScope();
  const { client, session } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNavigate();
  const intent = useIntentKey();
  const cats = useQuery({ queryKey: ["expenses", "categories", ...sc.scopeKey], queryFn: () => fetchCategories(client), retry: false });
  const payees = useQuery({ queryKey: ["expenses", "payees", ...sc.scopeKey], queryFn: () => fetchPayees(client), retry: false });
  const [categoryId, setCategoryId] = useState("");
  const [payeeId, setPayeeId] = useState("");
  const [newCat, setNewCat] = useState("");
  const [newPayee, setNewPayee] = useState("");
  const [date, setDate] = useState(sc.today);
  const [amount, setAmount] = useState("");
  const [tax, setTax] = useState("");
  const [mode, setMode] = useState("cash");
  const [status, setStatus] = useState<ExpenseStatus>("paid");
  const [invoice, setInvoice] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  const created = useRef<string | null>(null);
  // What this submit already made: a retry after the expense failed must not create them again (the API refuses a second category).
  const madeCategory = useRef<string | null>(null);
  const madePayee = useRef<string | null>(null);
  // New categories, new payees and attachments are each their own lever (D57), as their routes check.
  const can = { category: sc.ctx.has("expenses.categories_add_edit"), payee: sc.ctx.has("expenses.payees_add_edit"), attach: sc.ctx.has("expenses.attachments") };

  const amt = parseMoneyInput(amount, sc.currency, sc.locale);
  const taxMinor = tax.trim() === "" ? 0 : parseMoneyInput(tax, sc.currency, sc.locale);
  const fileErr = file && !TYPES.includes(file.type) ? "Only PDF, JPEG and PNG files." : file && file.size > MAX_BYTES ? "At most 10 MB." : null;
  const errs = {
    amount: amount === "" ? null : amt === null ? "Enter an amount like 1500 or 1500.50." : null,
    tax: taxMinor === null ? "Enter an amount, or leave empty." : null,
    date: isDate(date) ? null : "Use a date like 2026-11-02.",
  };
  const branchId = sc.monthBranchId;
  const ready = !!branchId && (!!categoryId || newCat.trim() !== "") && amt !== null && !errs.tax && !errs.date && !fileErr;

  const save = useMutation({
    mutationFn: async () => {
      let expenseId = created.current;
      if (!expenseId) {
        let cat = categoryId;
        if (!cat) cat = madeCategory.current ??= (await createCategory(client, newCat.trim())).category.id;
        let payee = payeeId;
        if (!payee && newPayee.trim()) payee = madePayee.current ??= (await createPayee(client, newPayee.trim())).payee.id;
        const r = await createExpense(client, {
          branchId: branchId!, categoryId: cat, expenseDate: date, amountExTax: amt!, ...(taxMinor ? { taxTotal: taxMinor } : {}),
          paymentMode: mode, status, ...(payee ? { payeeId: payee } : {}), ...(invoice.trim() ? { supplierInvoiceNo: invoice.trim() } : {}), ...(note.trim() ? { note: note.trim() } : {}),
        }, intent.key());
        expenseId = r.expense.id;
        created.current = expenseId;
        setCreatedId(expenseId);
        intent.renew();
      }
      if (file && can.attach) await uploadAttachment(session, expenseId, file);
      return expenseId;
    },
    onSuccess: async () => { toast.show("Expense added"); await qc.invalidateQueries({ queryKey: ["expenses"] }); nav("/expenses"); },
    onError: (e) => setErr(created.current ? `The expense was saved, but the attachment failed: ${errorText(e)}` : errorText(e)),
  });

  return (
    <div className={s.page}>
      <PageHead title="Add expense"><Button as={Link} to="/expenses" size="sm">Back</Button></PageHead>
      <Card aria-label="New expense">
        <CardHeader title="Details" subtitle="Enter the amounts exactly as the bill shows them." />
        <Gate query={cats} what="expense categories">
          {({ categories }) => (
            <div className={s.body}>
              {!branchId ? <p className={s.formErr}>Choose a branch in the switcher first.</p> : null}
              <div className={s.grid2}>
                <Field label="Category">{(a) => <select {...a} className={s.input} value={categoryId} onChange={(e) => { setCategoryId(e.target.value); madeCategory.current = null; }}><option value="">{categories.length ? "Choose category" : "No categories yet"}</option>{categories.filter((c) => !c.archived).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}</Field>
                {!categoryId && can.category ? <Field label="Or new category">{(a) => <input {...a} className={s.input} value={newCat} maxLength={80} onChange={(e) => { setNewCat(e.target.value); madeCategory.current = null; }} />}</Field> : null}
              </div>
              <div className={s.grid2}>
                <Field label="Paid to" hint="Optional">{(a) => <select {...a} className={s.input} value={payeeId} onChange={(e) => { setPayeeId(e.target.value); madePayee.current = null; }}><option value="">None</option>{(payees.data?.payees ?? []).filter((p) => !p.archived).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}</Field>
                {!payeeId && can.payee ? <Field label="Or new payee">{(a) => <input {...a} className={s.input} value={newPayee} maxLength={120} onChange={(e) => { setNewPayee(e.target.value); madePayee.current = null; }} />}</Field> : null}
              </div>
              <div className={s.grid2}>
                <Field label="Amount before tax" error={errs.amount}>{(a) => <MoneyInput {...a} value={amount} onChange={setAmount} />}</Field>
                <Field label="Tax on the bill" error={errs.tax} hint="Optional">{(a) => <MoneyInput {...a} value={tax} onChange={setTax} />}</Field>
              </div>
              <div className={s.grid2}>
                <Field label="Date" error={errs.date}>{(a) => <input {...a} className={s.input} value={date} onChange={(e) => setDate(e.target.value)} placeholder="YYYY-MM-DD" />}</Field>
                <Field label="Payment mode">{(a) => <select {...a} className={s.input} value={mode} onChange={(e) => setMode(e.target.value)}>{MODES.map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}</select>}</Field>
              </div>
              <div className={s.grid2}>
                <Field label="Status">{(a) => <select {...a} className={s.input} value={status} onChange={(e) => setStatus(e.target.value as ExpenseStatus)}><option value="paid">Paid</option><option value="unpaid">Unpaid</option></select>}</Field>
                <Field label="Supplier invoice no." hint="Optional">{(a) => <input {...a} className={s.input} value={invoice} maxLength={64} onChange={(e) => setInvoice(e.target.value)} />}</Field>
              </div>
              <Field label="Note" hint="Optional">{(a) => <textarea {...a} className={s.input} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />}</Field>
              {can.attach ? <Field label="Attachment" error={fileErr} hint="Bill or receipt: PDF, JPEG or PNG, up to 10 MB">{(a) => <input {...a} type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />}</Field> : null}
              {err ? <p className={s.formErr} role="alert">{err}</p> : null}
              <div className={s.actions}>
                <Button variant="primary" disabled={!ready} busy={save.isPending} busyLabel="Saving…" onClick={() => { setErr(null); save.mutate(); }}>{createdId ? "Retry attachment" : "Save expense"}</Button>
                <Button as={Link} to="/expenses">Cancel</Button>
              </div>
            </div>
          )}
        </Gate>
      </Card>
    </div>
  );
}
